import React, { useEffect, useRef, useState } from "react";
import { useTerminalConfig } from "../../Path/NewTerminalConfigs";
import { FaPause, FaPlay, FaTimes, FaTrash } from "react-icons/fa";
import {
  createTerminalAds,
  deleteTemplateAdsPoster,
  getAllAds,
  uploadTerminalImage,
  // uploadTerminalVideo, // TODO: uncomment once the dedicated video upload API exists
} from "../../Services/terminal";
import { base64ToFile } from "./TerminalForm";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { formatDateRangeInTZ } from "../../Services/datesHelper";
import { MAX_VIDEO_UPLOAD_SIZE, MAX_IMAGE_UPLOAD_SIZE } from "../../Constants/constant";

const DISABLED_PREPLANNING_CITY_IDS = (
  process.env.REACT_APP_DISABLE_POSTER_PREPLANNING_CITY_IDS || ""
)
  .split(",")
  .map((s) => parseInt(s.trim(), 10))
  .filter(Boolean);

const VideoCard = ({ src }) => {
  return (
    <div className="relative w-full">
      <video
        src={src}
        className="w-full max-h-48 object-contain rounded"
        controls
      />
    </div>
  );
};

export default function AdsPosterManager() {
  const { terminalConfig, setTerminalConfig } = useTerminalConfig();
  const isPreplanningDisabled = DISABLED_PREPLANNING_CITY_IDS.includes(
    Number(terminalConfig?.cityId)
  );
  const { t } = useTranslation();
  const [allAds, setAllAds] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [loader, setLoader] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDeletingId, setIsDeletingId] = useState();

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };
  const initialAd = {
    title: "",
    image: null,  // base64 string for image upload
    video: null,  // { file: File, previewUrl: string } for video upload
    adType: "always",
    activeFrom: "",
    activeTo: "",
    slotStart: "",
    slotEnd: "",
  };
  const [newAd, setNewAd] = useState(initialAd);

  const slotToMinutes = (hhmm) => {
    const [h, m] = hhmm.split(":").map(Number);
    return h * 60 + m;
  };

  const handleAddAd = async () => {
    if (!newAd.title.trim() || (!newAd.image && !newAd.video) || loader) return;
    if (!isPreplanningDisabled) {
      if (newAd.adType === "date_range") {
        if (newAd.activeFrom && newAd.activeTo && new Date(newAd.activeTo) <= new Date(newAd.activeFrom)) {
          setErrorMessage(t("ad_date_invalid"));
          return;
        }
      }
      if (newAd.adType === "daily_slot") {
        if (!newAd.slotStart || !newAd.slotEnd) {
          setErrorMessage(t("ad_slot_invalid"));
          return;
        }
        if (slotToMinutes(newAd.slotEnd) <= slotToMinutes(newAd.slotStart)) {
          setErrorMessage(t("ad_slot_invalid"));
          return;
        }
      }
    }
    let res;
    let adId;
    let wasCancelled = false;
    try {
      setLoader(true);
      setUploadProgress(0);
      let adPayload;
      if (isPreplanningDisabled) {
        adPayload = {
          title: newAd.title,
          terminalId: terminalConfig.terminalId,
          adType: "always",
          activeFrom: null,
          activeTo: null,
          slotStart: null,
          slotEnd: null,
        };
      } else {
        adPayload = {
          title: newAd.title,
          terminalId: terminalConfig.terminalId,
          adType: newAd.adType,
        };
        if (newAd.adType === "date_range") {
          if (newAd.activeFrom) adPayload.activeFrom = new Date(newAd.activeFrom).toISOString();
          if (newAd.activeTo) adPayload.activeTo = new Date(newAd.activeTo).toISOString();
        }
        if (newAd.adType === "daily_slot") {
          adPayload.slotStart = newAd.slotStart;
          adPayload.slotEnd = newAd.slotEnd;
        }
      }
      res = await createTerminalAds(adPayload);
      // Backend wraps the created ad in a { status, data } envelope; the id now
      // lives at res.data.data.id. Fall back to res.data.id for the old shape.
      adId = res?.data?.data?.id ?? res?.data?.id;
      if (!adId) {
        // No valid id: stop here so we never call /terminalsAds/undefined/image.
        setErrorMessage(t("ad_error"));
        throw new Error("missing-ad-id");
      }
      const formData = new FormData();
      let uploadRes;
      if (newAd.image) {
        formData.append("image", base64ToFile(newAd.image));
      } else {
        // TODO: switch to uploadTerminalVideo once the dedicated endpoint exists
        formData.append("image", newAd.video.file);
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;
      uploadRes = await uploadTerminalImage(adId, formData, {
        signal: controller.signal,
        onUploadProgress: (e) => {
          if (e.total) {
            setUploadProgress(Math.round((e.loaded * 100) / e.total));
          }
        },
      });
      if (
        res.data.status !== "success" ||
        uploadRes.data.status !== "success"
      ) {
        // Upload resolved at the HTTP level but the backend did not confirm
        // success: treat as a failure so the orphan ad gets cleaned up below.
        setErrorMessage(t("ad_error"));
        throw new Error("upload-not-confirmed");
      }
      setSuccessMessage(t("ad_sucess"));
      getAllAds(terminalConfig?.terminalId).then((res) =>
        setAllAds(res.data?.data)
      );
    } catch (error) {
      if (axios.isCancel(error)) {
        // User aborted the upload.
        wasCancelled = true;
      } else if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || t("ad_error");
        setErrorMessage(message);
      } else {
        setErrorMessage(t("ad_error"));
      }
      // Cancel or upload failure: remove the orphan ad record created above so
      // we never leave a titled-but-media-less ad behind. (When adId is falsy
      // the create step itself failed, so there is nothing to clean up.)
      if (adId) {
        deleteTemplateAdsPoster(adId).catch(() => {});
      }
    } finally {
      abortControllerRef.current = null;
    }
    if (newAd.video?.previewUrl) URL.revokeObjectURL(newAd.video.previewUrl);
    setUploadProgress(0);
    if (wasCancelled) {
      // Abort: tear down immediately, no success/error window to wait on.
      setLoader(false);
    } else {
      setTimeout(() => {
        setErrorMessage("");
        setSuccessMessage("");
        setLoader(false);
      }, 2000);
    }
    setNewAd(initialAd);
    // Reset the file input's DOM value too; clearing only React state leaves the
    // previously picked file in the input, so re-selecting the SAME file fires no
    // change event and the button stays disabled until a page reload.
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    // Allow overriding the video cap via localStorage (value in bytes);
    // fall back to the constant when the key is absent or not a valid number.
    const storedVideoMax = parseInt(
      window.localStorage.getItem("maxVideoUploadSize"),
      10
    );
    const videoMaxSize =
      Number.isFinite(storedVideoMax) && storedVideoMax > 0
        ? storedVideoMax
        : MAX_VIDEO_UPLOAD_SIZE;
    const maxSize = isVideo ? videoMaxSize : MAX_IMAGE_UPLOAD_SIZE;
    if (file.size > maxSize) {
      setErrorMessage(t("file_size_error", { size: maxSize / 1024 / 1024 }));
      return;
    }
    // Revoke any previous video object URL before replacing
    if (newAd.video?.previewUrl) URL.revokeObjectURL(newAd.video.previewUrl);
    setErrorMessage("");
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        setNewAd((prev) => ({ ...prev, image: reader.result, video: null }));
      };
      reader.readAsDataURL(file);
    } else if (file.type.startsWith("video/")) {
      const previewUrl = URL.createObjectURL(file);
      setNewAd((prev) => ({ ...prev, video: { file, previewUrl }, image: null }));
    }
  };

  const clearMedia = () => {
    if (newAd.video?.previewUrl) URL.revokeObjectURL(newAd.video.previewUrl);
    setNewAd({ ...newAd, image: null, video: null });
    fileInputRef.current.value = null;
  };

  // The "X" on the media preview aborts an in-flight upload, otherwise clears
  // the selected media as before.
  const handleCancelOrClear = () => {
    if (loader) {
      abortControllerRef.current?.abort();
      return;
    }
    clearMedia();
  };

  useEffect(() => {
    getAllAds(terminalConfig?.terminalId).then((res) =>
      setAllAds(res.data?.data)
    );
  }, []);

  const removePoster = async (addId) => {
    setIsDeletingId(addId);
    try {
      const response = await deleteTemplateAdsPoster(addId);
      console.log("🚀 ~ removePoster ~ response:", response);
      if (response.status === 200) {
        setAllAds(allAds.filter((ad) => ad.id !== addId));
        // Drop the deleted ad from the saved config selection too, otherwise the
        // dead id is still sent in adIds and the backend rejects it
        // (INVALID_ADVERTISEMENT) when saving the terminal config.
        setTerminalConfig((prev) => ({
          ...prev,
          adIds: prev.adIds?.filter((id) => id !== addId) ?? [],
        }));
        toast.success(t("template_c_ad_removed"));
      } else {
        throw new Error(t("template_c_ad_error"));
      }
    } catch (err) {
      console.error(err);
      toast.error(t("template_c_ad_error"));
    } finally {
      setIsDeletingId(undefined);
    }
  };

  return (
    <div className="mt-6">
      <h2 className="text-2xl font-bold text-white">{t("add_ad_poster")}</h2>

      <div className="grid grid-cols-1 gap-4">
        <input
          type="text"
          placeholder={t("titleLabel")}
          className="w-full px-4 py-2 border border-gray-300 rounded max-w-sm"
          value={newAd.title}
          onChange={(e) =>
            setNewAd((prev) => ({ ...prev, title: e.target.value }))
          }
        />
        {!isPreplanningDisabled && (
          <>
            <div className="max-w-sm">
              <label className="text-white text-sm mb-1 block">{t("ad_type")}</label>
              <select
                value={newAd.adType}
                onChange={(e) =>
                  setNewAd((prev) => ({
                    ...prev,
                    adType: e.target.value,
                    activeFrom: "",
                    activeTo: "",
                    slotStart: "",
                    slotEnd: "",
                  }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded"
              >
                <option value="always">{t("ad_type_always")}</option>
                <option value="date_range">{t("ad_type_date_range")}</option>
                <option value="daily_slot">{t("ad_type_daily_slot")}</option>
              </select>
            </div>
            {newAd.adType === "date_range" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-sm">
                <div>
                  <label className="text-white text-sm mb-1 block">{t("ad_active_from")}</label>
                  <Flatpickr
                    options={{ enableTime: true, dateFormat: "Y-m-d H:i", minDate: "today" }}
                    value={newAd.activeFrom}
                    onChange={([date]) => {
                      setNewAd((prev) => ({ ...prev, activeFrom: date || "" }));
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded"
                    placeholder={t("ad_active_from")}
                  />
                </div>
                <div>
                  <label className="text-white text-sm mb-1 block">{t("ad_active_to")}</label>
                  <Flatpickr
                    options={{ enableTime: true, dateFormat: "Y-m-d H:i", minDate: "today" }}
                    value={newAd.activeTo}
                    onChange={([date]) => {
                      setNewAd((prev) => ({ ...prev, activeTo: date || "" }));
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded"
                    placeholder={t("ad_active_to")}
                  />
                </div>
              </div>
            )}
            {newAd.adType === "daily_slot" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-sm">
                <div>
                  <label className="text-white text-sm mb-1 block">{t("ad_slot_start")}</label>
                  <Flatpickr
                    options={{
                      enableTime: true,
                      noCalendar: true,
                      dateFormat: "H:i",
                      time_24hr: true,
                    }}
                    value={newAd.slotStart}
                    onChange={([date]) => {
                      if (!date) {
                        setNewAd((prev) => ({ ...prev, slotStart: "" }));
                        return;
                      }
                      const hh = String(date.getHours()).padStart(2, "0");
                      const mm = String(date.getMinutes()).padStart(2, "0");
                      setNewAd((prev) => ({ ...prev, slotStart: `${hh}:${mm}` }));
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded"
                    placeholder={t("ad_slot_start")}
                  />
                </div>
                <div>
                  <label className="text-white text-sm mb-1 block">{t("ad_slot_end")}</label>
                  <Flatpickr
                    options={{
                      enableTime: true,
                      noCalendar: true,
                      dateFormat: "H:i",
                      time_24hr: true,
                    }}
                    value={newAd.slotEnd}
                    onChange={([date]) => {
                      if (!date) {
                        setNewAd((prev) => ({ ...prev, slotEnd: "" }));
                        return;
                      }
                      const hh = String(date.getHours()).padStart(2, "0");
                      const mm = String(date.getMinutes()).padStart(2, "0");
                      setNewAd((prev) => ({ ...prev, slotEnd: `${hh}:${mm}` }));
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded"
                    placeholder={t("ad_slot_end")}
                  />
                </div>
              </div>
            )}
          </>
        )}
        {newAd.image ? (
          <div className="relative bg-gray-100 rounded w-32 h-32 overflow-hidden">
            <FaTimes
              className="absolute top-1 right-1 text-red-600 bg-white rounded-full p-1 cursor-pointer"
              onClick={handleCancelOrClear}
              size={18}
            />
            <img
              src={newAd.image}
              alt="Preview"
              className="object-contain w-full h-full"
            />
          </div>
        ) : newAd.video ? (
          <div className="relative bg-black rounded w-32 h-32 overflow-hidden">
            <FaTimes
              className="absolute top-1 right-1 z-10 text-red-600 bg-white rounded-full p-1 cursor-pointer"
              onClick={handleCancelOrClear}
              size={18}
            />
            <video
              src={newAd.video.previewUrl}
              className="object-contain w-full h-full"
              controls
            />
          </div>
        ) : (
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded-button w-fit rounded"
            onClick={handleButtonClick}
          >
            {t("uploadAdLogo")}
          </button>
        )}
        <input
          type="file"
          className="hidden"
          ref={fileInputRef}
          accept="image/png,image/jpeg,video/mp4,video/webm,video/quicktime"
          onChange={handleFileChange}
        />
        {loader && (
          <div className="max-w-sm">
            <div className="flex justify-between text-xs text-white mb-1">
              <span>{t("uploading")}</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-300 rounded-full h-2 overflow-hidden">
              <div
                className="bg-blue-600 h-full transition-all duration-200"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}
        {successMessage && (
          <span className="text-green-600 text-bold">{successMessage}</span>
        )}
        {errorMessage && (
          <span className="text-red-600 text-bold">{errorMessage}</span>
        )}
        <button
          onClick={handleAddAd}
          disabled={loader || !(newAd.title && (newAd.image || newAd.video))}
          className="w-fit px-4 py-2 bg-blue-600 text-white rounded hover:bg-indigo-600 disabled:bg-gray-100 disabled:text-gray-400 disabled:font-black"
        >
          {loader ? (
            <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin"></div>
          ) : (
            t("ad_poster")
          )}
        </button>
      </div>
      <h6 className="text-xl font-semibold mb-2 text-white w-full mt-6">
        {t("featured_posters")}
      </h6>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        {allAds?.map((ad, idx) => (
          <div
            key={idx}
            className="relative p-4 border rounded shadow-sm bg-gray-50 cursor-pointer"
            style={{
              border: terminalConfig?.adIds?.includes(ad.id)
                ? "5px solid green"
                : "",
            }}
            onClick={() => {
              if (terminalConfig.adIds.includes(ad.id)) {
                setTerminalConfig((prev) => ({
                  ...prev,
                  adIds: terminalConfig.adIds.filter((id) => id !== ad.id),
                }));
              } else {
                setTerminalConfig((prev) => ({
                  ...prev,
                  adIds:
                    terminalConfig.adIds?.length > 0
                      ? [...terminalConfig.adIds, ad.id]
                      : [ad.id],
                }));
              }
            }}
          >
            {isDeletingId === ad.id ? (
              <div className="absolute top-4 right-4 w-6 h-6 rounded-full border-2 border-t-transparent border-gray-500  animate-spin" />
            ) : (
              <FaTrash
                onClick={(e) => {
                  // Stop the click bubbling to the card, which would otherwise
                  // toggle this ad into adIds right before we delete it.
                  e.stopPropagation();
                  removePoster(ad.id);
                }}
                className="absolute top-2 right-2 h-6 w-6 m-2 text-red-500 hover:text-red-700 rounded"
              />
            )}
            <h4 className="text-lg font-semibold mb-1">{ad.title}</h4>
            {(() => {
              const resolvedType =
                ad.adType ||
                (ad.activeFrom || ad.activeTo ? "date_range" : "always");
              const chipLabel =
                resolvedType === "always"
                  ? t("ad_type_always")
                  : resolvedType === "date_range"
                  ? t("ad_type_date_range")
                  : t("ad_type_daily_slot");
              let summary = t("ad_always_active");
              if (resolvedType === "date_range") {
                summary =
                  formatDateRangeInTZ(ad.activeFrom, ad.activeTo) ||
                  `${ad.activeFrom ? t("ad_active_from") + ": " + new Date(ad.activeFrom).toLocaleString() : ""} ${ad.activeTo ? t("ad_active_to") + ": " + new Date(ad.activeTo).toLocaleString() : ""}`.trim();
              } else if (resolvedType === "daily_slot") {
                summary = `${t("ad_daily_slot")}: ${ad.slotStart || "--:--"} – ${ad.slotEnd || "--:--"}`;
              }
              return (
                <>
                  <span className="inline-block text-[10px] uppercase tracking-wide bg-gray-200 text-gray-700 rounded px-2 py-0.5 mb-2">
                    {chipLabel}
                  </span>
                  <p className="text-xs text-gray-500 mb-2">{summary}</p>
                </>
              );
            })()}
            {ad.videoUrl ? (
              <VideoCard src={ad.videoUrl} />
            ) : ad.imageUrl ? (
              <img
                src={ad.imageUrl}
                alt={ad.title}
                className="w-full h-auto max-h-48 object-contain rounded"
              />
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
